import AppPopup from '../../../components/AppPopup.vue';
import AppChatMessageReadPopup from '../../../components/chat/AppChatMessageReadPopup.vue';
import {getUsername} from '../../../filters/username';
import {deepCopyObject, sendPopupRequest} from '../../../utils/portuna';
import {Popup, popupDefaultCancelButton, popupDefaultCloseButton} from '../../popup';
import {removeSurnameAffix} from '../../utils';

export const CHAT_MESSAGE_TYPE_MESSAGE = 1;

export const CHAT_MESSAGE_TYPE_ADDED = 2;

export const CHAT_MESSAGE_TYPE_REMOVED = 3;

export const CHAT_MESSAGE_TYPE_TITLE_CHANGED = 4;

export const CHAT_MESSAGE_TYPE_ACTIVATED = 5;

export const CHAT_MESSAGE_TYPE_DEACTIVATED = 6;

export const CHAT_MESSAGE_TYPE_CREATED = 7;

export const CHAT_MESSAGE_TYPE_LEFT = 8;

export const CHAT_MESSAGE_TYPE_ENTITY_ADDED = 9;

export const CHAT_MESSAGE_TYPE_ENTITY_REMOVED = 10;

function deleteChatPopup(chat: Chat): SendPopupPromise
{
    return sendPopupRequest({
        type: 'delete',
        url: '/chat/chat/delete-chat',
        data: {
            id: chat.id,
        },
        message: {
            headline: 'Chat löschen',
            message: `Sind Sie sicher, dass Sie den Chat <strong>${chat.name}</strong> löschen möchten?`,
            success: 'Der Chat wurde erfolgreich gelöscht.',
            failed: 'Der Chat konnte nicht gelöscht werden.',
            loader: 'Chat wird gelöscht...',
        },
        options: {
            textIcon: 'delete_forever',
            textIconClass: 'text-danger',
            titleIcon: 'delete',
            buttonOkay: {
                title: 'Löschen',
                type: 'danger',
                icon: 'delete',
            },
        },
    });
}

function changeChatStatusPopup(chat: Chat): SendPopupPromise
{
    let isActive = chat.active;

    let message = {
        headline: isActive ? 'Chat deaktivieren' : 'Chat aktivieren',
        message: `Sind Sie sicher, dass Sie den Chat <strong>${chat.name}</strong> ${(isActive ? 'deaktivieren' : 'aktivieren')} möchten?`,
        success: isActive ? 'Der Chat wurde deaktiviert' : 'Der Chat wurde aktiviert',
        failed: isActive ? 'Chat konnte nicht deaktiviert werden' : 'Chat konnte nicht aktiviert werden',
        loader: isActive ? 'Chat wird deaktiviert ...' : 'Chat wird aktiviert ...',
    };

    const icon = isActive ? 'remove_circle' : 'check_circle';

    return sendPopupRequest({
        type: 'changeStatus' + (isActive ? 'Deactivate' : 'Activate'),
        url: '/chat/chat/toggle-chat-active-state',
        data: {
            chatId: chat.id,
        },
        message: message,
        options: {
            textIcon: icon,
            textIconClass: icon,
            titleIcon: icon,
            buttonOkay: {
                title: isActive ? 'Deaktivieren' : 'Aktivieren',
                type: isActive ? 'danger' : 'success',
                icon,
            },
        },
    });
}

function leaveChatPopup(chat: Chat): SendPopupPromise
{
    return sendPopupRequest({
        type: 'delete',
        url: '/chat/chat/exit',
        data: {
            chatId: chat.id,
        },
        message: {
            headline: 'Chat verlassen',
            message: `Sind Sie sicher, dass Sie den Chat <strong>${chat.name}</strong> verlassen möchten?`,
            success: 'Sie haben den Chat verlassen.',
            failed: 'Es ist ein Fehler aufgetreten.',
            loader: 'Chat wird verlassen...',
        },
        options: {
            textIcon: 'exit_to_app',
            textIconClass: 'exit_to_app',
            titleIcon: 'exit_to_app',
            buttonOkay: {
                title: 'Verlassen',
                type: 'danger',
                icon: 'exit_to_app',
            },
        },
    });
}

function removeParticipantPopup(chat: Chat, userId: number): SendPopupPromise
{
    const username = getUsername(userId);

    return sendPopupRequest({
        type: 'delete',
        url: '/chat/chat/remove-from-chat',
        data: {
            chatId: chat.id,
            userId: userId,
        },
        message: {
            headline: 'Teilnehmer entfernen',
            message: '<strong>Achtung!</strong> Möchten Sie <strong>' + username + '</strong> wirklich aus dem Chat <strong>' + chat.name + '</strong> entfernen?',
            success: username + ' wurde aus dem Chat entfernt.',
            failed: 'Der Teilnehmner konnte nicht entfernt werden',
            loader: 'Teilnehmer wird entfernt ...',
        },
        options: {
            textIcon: 'warning',
            textIconClass: 'text-danger',
            titleIcon: 'warning',
            buttonOkay: {
                title: 'Entfernen',
                type: 'danger',
                icon: 'warning',
            },
        },
    });
}

function discardMessagePopup(action: 'change' | 'close'): Promise<void>
{
    return new Promise((resolve, reject) => {
        const popup = new Popup({
            title: 'Nachricht verwerfen',
            titleIcon: 'delete',
            text: `Im aktuellen Chat existiert eine ungesendete Nachricht. Wenn Sie den Chat ${action === 'close' ? 'schließen' : 'wechseln'} wird die Nachricht verworfen. Wollen Sie den Chat ${action === 'close' ? 'schließen' : 'wechseln'} und die Nachricht verwerfen?`,
            textIcon: 'delete',
            textIconClass: 'text-danger',
            size: 'small',
            buttons: [
                Object.assign({}, popupDefaultCancelButton, {
                    cmd(popup: AppPopup, event: MouseEvent)
                    {
                        // @ts-ignore tmp build fix
                        popup.close(event);
                        reject();
                    },
                }),
                {
                    type: 'danger',
                    title: 'Nachricht verwerfen',
                    icon: 'delete',
                    cmd: popup =>
                    {
                        popup.close();
                        resolve();
                    },
                },
            ]
        });

        popup.open();
    });
}

function messageReadPopup(messageId: number)
{
    const popup = new Popup({
        title: 'Nachricht gelesen von',
        titleIcon: 'done_all',
        component: AppChatMessageReadPopup,
        componentProps: {messageId},
        size: 'small',
        buttons: [popupDefaultCloseButton],
    });

    popup.open();
}

function sortParticipants(participants: ChatParticipant[])
{
    const chatParticipants: InternalChatParticipant[] = deepCopyObject(participants || []);

    for (let participant of chatParticipants)
    {
        // only required for sort - so remove affix
        participant._sortHelper = removeSurnameAffix(participant.surname);
    }

    return chatParticipants.sort((a, b) => a._sortHelper!.toLocaleLowerCase().localeCompare(b._sortHelper!.toLocaleLowerCase()));
}

export {
    deleteChatPopup,
    changeChatStatusPopup,
    leaveChatPopup,
    removeParticipantPopup,
    discardMessagePopup,
    messageReadPopup,
    sortParticipants,
};